import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { T } from '@/design/tokens';
import { Button } from './Button';

/**
 * Modal — reservado para AÇÃO que precisa de foco total (confirmar, criar).
 * Consulta usa ContextPanel; esta é a divisão de água do AMAI OPS.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="overlay overlay--modal" onClick={onClose} aria-hidden />
      <div className={`modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal__head">
          <div className="modal__title">{title}</div>
          <button type="button" className="ctxpanel__close" onClick={onClose} aria-label="Fechar">
            <X size={18} aria-hidden />
          </button>
        </div>
        {description && <div className="modal__description">{description}</div>}
        {children}
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </>
  );
}

/** Painel lateral para FORMULÁRIO longo — mais largo que o ContextPanel. */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="overlay overlay--modal" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="ctxpanel__head" style={{ paddingBottom: 16 }}>
          <div className="ctxpanel__titlerow">
            <div>
              <div className="ctxpanel__title">{title}</div>
              {description && <div className="ctxpanel__subtitle">{description}</div>}
            </div>
            <button type="button" className="ctxpanel__close" onClick={onClose} aria-label="Fechar">
              <X size={18} aria-hidden />
            </button>
          </div>
        </div>
        <div className="ctxpanel__body">{children}</div>
        {footer && <div className="ctxpanel__footer">{footer}</div>}
      </aside>
    </>
  );
}

/**
 * ConfirmDialog para ações destrutivas ou irreversíveis.
 *
 * Quando `requireReason` está ligado, a justificativa é obrigatória e vai para
 * a trilha de auditoria — é assim que estorno, cancelamento e ajuste de estoque
 * ficam explicáveis depois.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirmar',
  destructive,
  requireReason,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  requireReason?: boolean;
  loading?: boolean;
  /** Recusa vinda da API — o operador precisa ler o motivo sem fechar a caixa. */
  error?: string;
}) {
  const [reason, setReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  // Abrir de novo começa com a justificativa em branco.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setReason('');
  }

  const blocked = requireReason && reason.trim().length < 5;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => onConfirm(requireReason ? reason.trim() : undefined)}
            disabled={blocked}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {destructive && <AlertTriangle size={20} color={T.red} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />}
        <div style={{ fontSize: 13.5, color: T.inkSoft, lineHeight: 1.5 }}>{message}</div>
      </div>

      {requireReason && (
        <label className="field" style={{ marginTop: 18 }}>
          <span className="field__label">Justificativa (fica registrada na auditoria)</span>
          <textarea
            className="input"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explique o motivo desta ação…"
          />
          {blocked && <span className="field__hint">Escreva ao menos 5 caracteres.</span>}
        </label>
      )}
    </Modal>
  );
}
